import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Wrench, DollarSign, Star, LogOut, Send, Clock, CheckCircle, Calendar, 
  FileText, TrendingUp, Users, MapPin, ChevronRight, BarChart3, ArrowUpRight, 
  ArrowDownRight, X, Eye, Car, User, History, Filter, SortDesc, ChevronDown, 
  ChevronUp, Phone, Mail, AlertCircle, Gauge, Sparkles, Timer, MessageCircle, Shield, HelpCircle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import LocationMap from '../components/LocationMap';
import { TourGuide } from '../components/TourGuide';
import { NotificationsDropdown } from '../components/NotificationsDropdown';

const TALLER_TOUR_STEPS = [
  { selector: '[data-testid="kpi-offers"]', title: 'Tus métricas del mes', text: 'Ofertas enviadas, aceptadas, citas e ingresos. Haz clic en cualquier KPI para ver el detalle.' },
  { selector: '[data-testid="taller-tabs"]', title: 'Solicitudes cercanas', text: 'Aquí llegan las solicitudes de clientes en tu zona. Responde con tu oferta en minutos y gana nuevos clientes.' },
  { selector: '[data-testid="tab-reports"]', title: 'Reportes de tu negocio', text: 'Estadísticas de conversión, ingresos y satisfacción de tus clientes, exportables a CSV.' },
  { selector: '[data-testid="tab-settings"]', title: 'Tu perfil público', text: 'Configura tus servicios, horarios y tipos de vehículo que atiendes. Tu reputación atrae más solicitudes.' },
  { selector: null, title: '¡Tu taller está listo!', text: 'Cada trabajo completado suma reseñas verificadas y alimenta el Car Passport de tus clientes. Repite el tour con el botón de ayuda.' },
];
import RepairTimeline from '../components/RepairTimeline';
import TallerReportsTab from '../components/TallerReportsTab';
import TallerSettingsTab from '../components/TallerSettingsTab';
import ChatPreview from '../components/ChatPreview';
import DamageValuationForm from '../components/DamageValuationForm';

const TallerDashboard = () => {
  const { user, API, sessionToken, handleLogout } = useContext(AuthContext);
  const [serviceRequests, setServiceRequests] = useState([]);
  const [tourActive, setTourActive] = useState(false);
  const [allRequests, setAllRequests] = useState([]);
  const [myBids, setMyBids] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [bidForm, setBidForm] = useState({ price: '', message: '', estimated_time: '', proposed_dates: [] });
  const [newProposedDate, setNewProposedDate] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Estado para filtros de solicitudes
  const [serviceTypeFilter, setServiceTypeFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('newest'); // 'newest', 'oldest'
  const [expandedRequest, setExpandedRequest] = useState(null);
  const [responseStats, setResponseStats] = useState(null);
  
  // Estado para el modal de estadísticas detalladas
  const [statsModalOpen, setStatsModalOpen] = useState(false);
  const [selectedKPI, setSelectedKPI] = useState(null);
  const [statsTimeRange, setStatsTimeRange] = useState('month'); // 'week', 'month', 'all'

  // Estado para expandir progreso de reparación en citas
  const [expandedAppointment, setExpandedAppointment] = useState(null);

  // Estado para chat preview
  const [chatOpen, setChatOpen] = useState(false);
  const [chatClientName, setChatClientName] = useState('');

  // Estado para valuación de daños
  const [valuationModalOpen, setValuationModalOpen] = useState(false);
  const [selectedRequestForValuation, setSelectedRequestForValuation] = useState(null);

  const fetchResponseStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/taller/response-stats`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setResponseStats(res.data);
    } catch (error) {
      console.error('Error fetching response stats:', error);
    }
  }, [API, sessionToken]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [requestsRes, bidsRes, appointmentsRes] = await Promise.all([
        axios.get(`${API}/service-requests`, { headers: { Authorization: `Bearer ${sessionToken}` } }),
        axios.get(`${API}/bids`, { headers: { Authorization: `Bearer ${sessionToken}` } }),
        axios.get(`${API}/appointments`, { headers: { Authorization: `Bearer ${sessionToken}` } })
      ]);

      setAllRequests(requestsRes.data);
      setServiceRequests(requestsRes.data.filter(r => r.status === 'open'));
      setMyBids(bidsRes.data);
      setAppointments(appointmentsRes.data);
    } catch (error) {
      console.error('Error fetching taller data:', error);
    } finally {
      setLoading(false);
    }
  }, [API, sessionToken]);

  const markRequestAsViewed = useCallback(async (requestId) => {
    try {
      await axios.post(
        `${API}/service-requests/${requestId}/view`,
        {},
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );
      // Update local state to mark as viewed
      setServiceRequests(prev => prev.map(r => 
        r.id === requestId ? { ...r, is_new: false } : r
      ));
    } catch (error) {
      console.error('Error marking request as viewed:', error);
    }
  }, [API, sessionToken]);

  useEffect(() => {
    fetchData();
    fetchResponseStats();
  }, [fetchData, fetchResponseStats]);

  // Tipos de servicio disponibles para filtro
  const serviceTypes = [
    { value: 'all', label: 'Todos los servicios' },
    { value: 'Cambio de aceite', label: 'Cambio de aceite' },
    { value: 'Cambio de frenos', label: 'Cambio de frenos' },
    { value: 'Cambio de llantas', label: 'Cambio de llantas' },
    { value: 'Alineación y balanceo', label: 'Alineación y balanceo' },
    { value: 'Revisión general', label: 'Revisión general' },
    { value: 'Diagnóstico', label: 'Diagnóstico' },
    { value: 'Reparación de motor', label: 'Reparación de motor' },
    { value: 'Sistema eléctrico', label: 'Sistema eléctrico' },
    { value: 'Aire acondicionado', label: 'Aire acondicionado' },
    { value: 'Suspensión', label: 'Suspensión' },
    { value: 'Transmisión', label: 'Transmisión' },
    { value: 'Otro', label: 'Otro' }
  ];

  // Filtrar y ordenar solicitudes
  const filteredRequests = useMemo(() => {
    let filtered = [...serviceRequests];
    
    // Filtrar por tipo de servicio
    if (serviceTypeFilter !== 'all') {
      filtered = filtered.filter(r => 
        r.service_type?.toLowerCase().includes(serviceTypeFilter.toLowerCase())
      );
    }
    
    // Ordenar por fecha
    filtered.sort((a, b) => {
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });
    
    return filtered;
  }, [serviceRequests, serviceTypeFilter, sortOrder]);

  // Obtener tipos únicos de las solicitudes actuales
  const uniqueServiceTypes = useMemo(() => {
    const types = new Set(serviceRequests.map(r => r.service_type));
    return ['all', ...Array.from(types)];
  }, [serviceRequests]);

  // Memoized sorted lists for performance
  const sortedMyBids = useMemo(() => {
    return [...myBids].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [myBids]);

  const sortedAppointments = useMemo(() => {
    return [...appointments].sort((a, b) => new Date(b.scheduled_date) - new Date(a.scheduled_date));
  }, [appointments]);

  const handleSubmitBid = async (requestId) => {
    if (!bidForm.proposed_dates || bidForm.proposed_dates.length === 0) {
      toast.error('Agrega al menos una fecha propuesta para el cliente');
      return;
    }
    try {
      await axios.post(
        `${API}/bids`,
        {
          service_request_id: requestId,
          price: parseFloat(bidForm.price),
          message: bidForm.message,
          estimated_time: bidForm.estimated_time,
          proposed_dates: bidForm.proposed_dates,
        },
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );

      toast.success('Oferta enviada exitosamente');
      setBidForm({ price: '', message: '', estimated_time: '', proposed_dates: [] });
      setNewProposedDate('');
      setSelectedRequest(null);
      fetchData();
    } catch (error) {
      toast.error('Error al enviar oferta');
      console.error(error);
    }
  };

  const addProposedDate = () => {
    if (!newProposedDate) return;
    const iso = new Date(newProposedDate).toISOString();
    if (bidForm.proposed_dates.includes(iso)) {
      toast.error('Fecha ya añadida');
      return;
    }
    setBidForm({ ...bidForm, proposed_dates: [...bidForm.proposed_dates, iso] });
    setNewProposedDate('');
  };

  const removeProposedDate = (iso) => {
    setBidForm({ ...bidForm, proposed_dates: bidForm.proposed_dates.filter(d => d !== iso) });
  };

  // Funciones de utilidad para fechas
  const getStartOfWeek = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  };

  const getStartOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  };

  const isInTimeRange = (dateStr, range) => {
    if (!dateStr) return false;
    const date = new Date(dateStr);
    const now = new Date();
    
    if (range === 'week') {
      const startOfWeek = getStartOfWeek(now);
      return date >= startOfWeek;
    } else if (range === 'month') {
      const startOfMonth = getStartOfMonth(now);
      return date >= startOfMonth;
    }
    return true; // 'all'
  };

  // Filtrar datos por rango de tiempo
  const getFilteredBids = (range) => myBids.filter(b => isInTimeRange(b.created_at, range));
  const getFilteredAppointments = (range) => appointments.filter(a => isInTimeRange(a.scheduled_date || a.created_at, range));

  // Calcular estadísticas basadas en el rango de tiempo
  const calculateStats = (range) => {
    const filteredBids = getFilteredBids(range);
    const filteredAppointments = getFilteredAppointments(range);
    
    return {
      totalBids: filteredBids.length,
      pendingBids: filteredBids.filter(b => b.status === 'pending').length,
      acceptedBids: filteredBids.filter(b => b.status === 'accepted').length,
      rejectedBids: filteredBids.filter(b => b.status === 'rejected').length,
      scheduledAppointments: filteredAppointments.filter(a => a.status === 'scheduled').length,
      completedAppointments: filteredAppointments.filter(a => a.status === 'completed').length,
      totalRevenue: filteredBids.filter(b => b.status === 'accepted').reduce((sum, b) => sum + (b.price || 0), 0),
      conversionRate: filteredBids.length > 0 ? ((filteredBids.filter(b => b.status === 'accepted').length / filteredBids.length) * 100).toFixed(1) : 0
    };
  };

  // Estadísticas por período para el modal
  const getStatsByPeriod = (kpiType) => {
    const now = new Date();
    const periods = [];
    
    if (statsTimeRange === 'week') {
      // Últimas 4 semanas
      for (let i = 0; i < 4; i++) {
        const weekStart = new Date(now);
        weekStart.setDate(weekStart.getDate() - (i * 7) - weekStart.getDay() + 1);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);
        
        const weekBids = myBids.filter(b => {
          const d = new Date(b.created_at);
          return d >= weekStart && d <= weekEnd;
        });
        
        const weekAppointments = appointments.filter(a => {
          const d = new Date(a.scheduled_date || a.created_at);
          return d >= weekStart && d <= weekEnd;
        });
        
        let value = 0;
        switch (kpiType) {
          case 'offers':
            value = weekBids.length;
            break;
          case 'accepted':
            value = weekBids.filter(b => b.status === 'accepted').length;
            break;
          case 'appointments':
            value = weekAppointments.filter(a => a.status === 'scheduled' || a.status === 'completed').length;
            break;
          case 'revenue':
            value = weekBids.filter(b => b.status === 'accepted').reduce((sum, b) => sum + (b.price || 0), 0);
            break;
        }
        
        periods.push({
          label: `Sem ${weekStart.getDate()}/${weekStart.getMonth() + 1} - ${weekEnd.getDate()}/${weekEnd.getMonth() + 1}`,
          value,
          startDate: weekStart,
          endDate: weekEnd
        });
      }
    } else {
      // Últimos 6 meses
      for (let i = 0; i < 6; i++) {
        const monthStart = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const monthEnd = new Date(now.getFullYear(), now.getMonth() - i + 1, 0);
        
        const monthBids = myBids.filter(b => {
          const d = new Date(b.created_at);
          return d >= monthStart && d <= monthEnd;
        });
        
        const monthAppointments = appointments.filter(a => {
          const d = new Date(a.scheduled_date || a.created_at);
          return d >= monthStart && d <= monthEnd;
        });
        
        let value = 0;
        switch (kpiType) {
          case 'offers':
            value = monthBids.length;
            break;
          case 'accepted':
            value = monthBids.filter(b => b.status === 'accepted').length;
            break;
          case 'appointments':
            value = monthAppointments.filter(a => a.status === 'scheduled' || a.status === 'completed').length;
            break;
          case 'revenue':
            value = monthBids.filter(b => b.status === 'accepted').reduce((sum, b) => sum + (b.price || 0), 0);
            break;
        }
        
        const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
        periods.push({
          label: `${monthNames[monthStart.getMonth()]} ${monthStart.getFullYear()}`,
          value,
          startDate: monthStart,
          endDate: monthEnd
        });
      }
    }
    
    return periods.reverse();
  };

  // Estadísticas del mes actual
  const currentMonthStats = calculateStats('month');
  const previousMonthStats = calculateStats('all'); // Para comparar
  
  // Calcular variación porcentual
  const getVariation = (current, previous) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous * 100).toFixed(1);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'pending': return 'bg-orange-100 text-orange-800';
      case 'accepted': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'scheduled': return 'bg-purple-100 text-purple-800';
      case 'paid': return 'bg-green-100 text-green-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      'open': 'Abierta',
      'in_progress': 'En Progreso',
      'completed': 'Completado',
      'cancelled': 'Cancelada',
      'pending': 'Pendiente',
      'accepted': 'Aceptada',
      'rejected': 'Rechazada',
      'scheduled': 'Programada',
      'paid': 'Pagado'
    };
    return labels[status] || status;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('es-CR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-CR', {
      style: 'currency',
      currency: 'CRC',
      maximumFractionDigits: 0
    }).format(price);
  };

  const getCurrentMonthName = () => {
    return new Date().toLocaleDateString('es-CR', { month: 'long', year: 'numeric' });
  };

  // Abrir modal de estadísticas
  const openStatsModal = (kpiType) => {
    setSelectedKPI(kpiType);
    setStatsModalOpen(true);
  };

  const getKPITitle = (kpi) => {
    const titles = {
      'offers': 'Ofertas Enviadas',
      'accepted': 'Ofertas Aceptadas',
      'appointments': 'Citas',
      'revenue': 'Ingresos'
    };
    return titles[kpi] || kpi;
  };

  const getKPIIcon = (kpi) => {
    const icons = {
      'offers': <Send className="w-5 h-5" />,
      'accepted': <CheckCircle className="w-5 h-5" />,
      'appointments': <Calendar className="w-5 h-5" />,
      'revenue': <DollarSign className="w-5 h-5" />
    };
    return icons[kpi];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="glass-header">
        <div className="container mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-purple-600 rounded-xl flex items-center justify-center">
                <Wrench className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
                  {user?.company_name || user?.name || 'Mi Taller'}
                </h1>
                <div className="flex items-center space-x-2">
                  <Star className="w-4 h-4 text-yellow-500 fill-current" />
                  <span className="text-sm text-slate-600">{user?.rating?.toFixed(1) || '0.0'} ({user?.total_reviews || 0} reseñas)</span>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setTourActive(true)}
                className="text-gray-600 hover:text-purple-600"
                data-testid="start-tour-btn"
                title="Tour guiado"
              >
                <HelpCircle className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Tour</span>
              </Button>
              <NotificationsDropdown API={API} sessionToken={sessionToken} />
              <span className="text-sm text-slate-600 hidden md:inline">{user?.email}</span>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-600 hover:text-purple-600" data-testid="logout-btn">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        {/* Período actual */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
              Dashboard
            </h2>
            <p className="text-slate-600 flex items-center mt-1">
              <Calendar className="w-4 h-4 mr-2" />
              Datos de <span className="font-semibold text-purple-600 ml-1 capitalize">{getCurrentMonthName()}</span>
            </p>
          </div>
          <Badge className="bg-purple-100 text-purple-700 px-4 py-2">
            <BarChart3 className="w-4 h-4 mr-2" />
            Mes Actual
          </Badge>
        </div>

        {/* Stats Overview - KPIs Clickeables */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {/* KPI 1: Ofertas Enviadas */}
          <Card 
            className="p-4 border-slate-200 hover:shadow-lg hover:border-purple-300 transition-all cursor-pointer group"
            onClick={() => openStatsModal('offers')}
            data-testid="kpi-offers"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Send className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{currentMonthStats.totalBids}</p>
                  <p className="text-xs text-slate-500">Ofertas Enviadas</p>
                  <p className="text-xs text-purple-600 font-medium">Este mes</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-purple-600 transition-colors" />
            </div>
            <div className="mt-3 pt-3 border-t border-slate-100">
              <p className="text-xs text-slate-500 flex items-center">
                <Eye className="w-3 h-3 mr-1" />
                Click para ver detalle
              </p>
            </div>
          </Card>

          {/* KPI 2: Ofertas Aceptadas */}
          <Card 
            className="p-4 border-slate-200 hover:shadow-lg hover:border-green-300 transition-all cursor-pointer group"
            onClick={() => openStatsModal('accepted')}
            data-testid="kpi-accepted"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{currentMonthStats.acceptedBids}</p>
                  <p className="text-xs text-slate-500">Aceptadas</p>
                  <p className="text-xs text-green-600 font-medium">Este mes</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-green-600 transition-colors" />
            </div>
            <div className="mt-3 pt-3 border-t border-slate-100">
              <p className="text-xs text-slate-500 flex items-center">
                <Eye className="w-3 h-3 mr-1" />
                Click para ver detalle
              </p>
            </div>
          </Card>

          {/* KPI 3: Citas */}
          <Card 
            className="p-4 border-slate-200 hover:shadow-lg hover:border-purple-300 transition-all cursor-pointer group"
            onClick={() => openStatsModal('appointments')}
            data-testid="kpi-appointments"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Calendar className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{currentMonthStats.scheduledAppointments + currentMonthStats.completedAppointments}</p>
                  <p className="text-xs text-slate-500">Citas</p>
                  <p className="text-xs text-purple-600 font-medium">Este mes</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-purple-600 transition-colors" />
            </div>
            <div className="mt-3 pt-3 border-t border-slate-100">
              <p className="text-xs text-slate-500 flex items-center">
                <Eye className="w-3 h-3 mr-1" />
                Click para ver detalle
              </p>
            </div>
          </Card>

          {/* KPI 4: Ingresos */}
          <Card 
            className="p-4 border-slate-200 hover:shadow-lg hover:border-yellow-300 transition-all cursor-pointer group"
            onClick={() => openStatsModal('revenue')}
            data-testid="kpi-revenue"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                  <DollarSign className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-xl font-bold text-slate-900">{formatPrice(currentMonthStats.totalRevenue)}</p>
                  <p className="text-xs text-slate-500">Ingresos</p>
                  <p className="text-xs text-yellow-600 font-medium">Este mes</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-yellow-600 transition-colors" />
            </div>
            <div className="mt-3 pt-3 border-t border-slate-100">
              <p className="text-xs text-slate-500 flex items-center">
                <Eye className="w-3 h-3 mr-1" />
                Click para ver detalle
              </p>
            </div>
          </Card>
        </div>

        {/* Modal de Estadísticas Detalladas */}
        <Dialog open={statsModalOpen} onOpenChange={setStatsModalOpen}>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center space-x-2">
                {selectedKPI && getKPIIcon(selectedKPI)}
                <span>Estadísticas de {selectedKPI && getKPITitle(selectedKPI)}</span>
              </DialogTitle>
            </DialogHeader>
            
            <div className="mt-4">
              {/* Selector de período */}
              <div className="flex items-center justify-between mb-6">
                <p className="text-sm text-slate-600">Ver estadísticas por:</p>
                <div className="flex space-x-2">
                  <Button 
                    variant={statsTimeRange === 'week' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setStatsTimeRange('week')}
                    className={statsTimeRange === 'week' ? 'bg-purple-600' : ''}
                  >
                    Semana
                  </Button>
                  <Button 
                    variant={statsTimeRange === 'month' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setStatsTimeRange('month')}
                    className={statsTimeRange === 'month' ? 'bg-purple-600' : ''}
                  >
                    Mes
                  </Button>
                </div>
              </div>

              {/* Resumen */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <Card className="p-4 bg-purple-50 border-purple-200">
                  <p className="text-xs text-purple-600 uppercase font-medium">Este {statsTimeRange === 'week' ? 'Semana' : 'Mes'}</p>
                  <p className="text-2xl font-bold text-purple-700">
                    {selectedKPI === 'revenue' 
                      ? formatPrice(calculateStats(statsTimeRange === 'week' ? 'week' : 'month')[
                          selectedKPI === 'offers' ? 'totalBids' :
                          selectedKPI === 'accepted' ? 'acceptedBids' :
                          selectedKPI === 'appointments' ? 'scheduledAppointments' :
                          'totalRevenue'
                        ])
                      : calculateStats(statsTimeRange === 'week' ? 'week' : 'month')[
                          selectedKPI === 'offers' ? 'totalBids' :
                          selectedKPI === 'accepted' ? 'acceptedBids' :
                          selectedKPI === 'appointments' ? 'scheduledAppointments' :
                          'totalRevenue'
                        ]
                    }
                  </p>
                </Card>
                <Card className="p-4 bg-green-50 border-green-200">
                  <p className="text-xs text-green-600 uppercase font-medium">Total Histórico</p>
                  <p className="text-2xl font-bold text-green-700">
                    {selectedKPI === 'revenue' 
                      ? formatPrice(calculateStats('all')[
                          selectedKPI === 'offers' ? 'totalBids' :
                          selectedKPI === 'accepted' ? 'acceptedBids' :
                          selectedKPI === 'appointments' ? 'scheduledAppointments' :
                          'totalRevenue'
                        ])
                      : calculateStats('all')[
                          selectedKPI === 'offers' ? 'totalBids' :
                          selectedKPI === 'accepted' ? 'acceptedBids' :
                          selectedKPI === 'appointments' ? 'scheduledAppointments' :
                          'totalRevenue'
                        ]
                    }
                  </p>
                </Card>
                <Card className="p-4 bg-blue-50 border-blue-200">
                  <p className="text-xs text-blue-600 uppercase font-medium">Promedio</p>
                  <p className="text-2xl font-bold text-blue-700">
                    {selectedKPI === 'revenue' 
                      ? formatPrice(calculateStats('all').totalRevenue / Math.max(1, myBids.filter(b => b.status === 'accepted').length))
                      : (calculateStats('all')[
                          selectedKPI === 'offers' ? 'totalBids' :
                          selectedKPI === 'accepted' ? 'acceptedBids' :
                          'scheduledAppointments'
                        ] / 6).toFixed(1)
                    }
                    <span className="text-sm font-normal text-slate-500">/mes</span>
                  </p>
                </Card>
              </div>

              {/* Tabla de datos por período */}
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Período</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-slate-600">
                        {selectedKPI === 'revenue' ? 'Ingresos' : 'Cantidad'}
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-slate-600">% del Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {selectedKPI && getStatsByPeriod(selectedKPI).map((period) => {
                      const total = getStatsByPeriod(selectedKPI).reduce((sum, p) => sum + p.value, 0);
                      const percentage = total > 0 ? ((period.value / total) * 100).toFixed(1) : 0;
                      
                      return (
                        <tr key={`kpi-${selectedKPI}-${period.label}`} className="hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm text-slate-900">{period.label}</td>
                          <td className="px-4 py-3 text-right text-sm font-semibold text-slate-900">
                            {selectedKPI === 'revenue' ? formatPrice(period.value) : period.value}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end space-x-2">
                              <div className="w-16 bg-slate-200 rounded-full h-2">
                                <div 
                                  className="bg-purple-600 h-2 rounded-full" 
                                  style={{ width: `${Math.min(100, percentage)}%` }}
                                ></div>
                              </div>
                              <span className="text-sm text-slate-600 w-12 text-right">{percentage}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-purple-50">
                    <tr>
                      <td className="px-4 py-3 text-sm font-semibold text-purple-900">Total</td>
                      <td className="px-4 py-3 text-right text-sm font-bold text-purple-900">
                        {selectedKPI === 'revenue' 
                          ? formatPrice(getStatsByPeriod(selectedKPI).reduce((sum, p) => sum + p.value, 0))
                          : getStatsByPeriod(selectedKPI).reduce((sum, p) => sum + p.value, 0)
                        }
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-semibold text-purple-900">100%</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Performance Summary */}
        <Card className="p-6 mb-8 border-slate-200 bg-white">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900 flex items-center">
              <TrendingUp className="w-5 h-5 mr-2 text-purple-600" />
              Resumen de Rendimiento
            </h3>
            <Badge className="bg-slate-100 text-slate-600 text-xs">
              Datos históricos totales
            </Badge>
          </div>
          <div className="grid md:grid-cols-5 gap-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-purple-600">{calculateStats('all').conversionRate}%</p>
              <p className="text-sm text-slate-600">Tasa de Conversión</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-green-600">{calculateStats('all').completedAppointments}</p>
              <p className="text-sm text-slate-600">Trabajos Completados</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-orange-600">{calculateStats('all').pendingBids}</p>
              <p className="text-sm text-slate-600">Ofertas Pendientes</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-blue-600">{serviceRequests.length}</p>
              <p className="text-sm text-slate-600">Solicitudes Disponibles</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-1">
                <Timer className="w-5 h-5 text-cyan-600" />
                <p className="text-3xl font-bold text-cyan-600">
                  {responseStats?.avg_response_time_minutes || 0}
                </p>
              </div>
              <p className="text-sm text-slate-600">Tiempo Resp. (min)</p>
            </div>
          </div>
        </Card>

        {/* Indicador de solicitudes nuevas */}
        {serviceRequests.filter(r => r.is_new).length > 0 && (
          <Card className="p-4 mb-6 border-purple-200 bg-purple-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center animate-pulse">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-purple-900">
                    {serviceRequests.filter(r => r.is_new).length} solicitudes nuevas sin ver
                  </p>
                  <p className="text-sm text-purple-700">
                    Responde rápido para mejorar tu tiempo de respuesta promedio
                  </p>
                </div>
              </div>
              <Badge className="bg-purple-600 text-white">
                <Timer className="w-3 h-3 mr-1" />
                Meta: &lt;30 min
              </Badge>
            </div>
          </Card>
        )}

        {/* Main Content Tabs */}
        <Tabs defaultValue="available" className="space-y-6">
          <TabsList className="bg-white border border-slate-200 p-1 flex-wrap h-auto" data-testid="taller-tabs">
            <TabsTrigger value="available" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white">
              Solicitudes Disponibles ({filteredRequests.length})
            </TabsTrigger>
            <TabsTrigger value="bids" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white">
              Mis Ofertas ({myBids.length})
            </TabsTrigger>
            <TabsTrigger value="appointments" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white">
              Citas ({appointments.length})
            </TabsTrigger>
            <TabsTrigger value="reports" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white" data-testid="tab-reports">
              Reportes
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white" data-testid="tab-settings">
              Configuración
            </TabsTrigger>
          </TabsList>

          {/* Available Requests Tab */}
          <TabsContent value="available" className="space-y-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <h3 className="text-xl font-semibold text-slate-900">Solicitudes Disponibles para Cotizar</h3>
              
              {/* Filtros */}
              <div className="flex flex-wrap items-center gap-3">
                {/* Filtro por tipo de servicio */}
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-slate-500" />
                  <Select value={serviceTypeFilter} onValueChange={setServiceTypeFilter}>
                    <SelectTrigger className="w-[200px] bg-white" data-testid="filter-service-type">
                      <SelectValue placeholder="Tipo de servicio" />
                    </SelectTrigger>
                    <SelectContent>
                      {serviceTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Ordenar por fecha */}
                <div className="flex items-center gap-2">
                  <SortDesc className="w-4 h-4 text-slate-500" />
                  <Select value={sortOrder} onValueChange={setSortOrder}>
                    <SelectTrigger className="w-[160px] bg-white" data-testid="sort-order">
                      <SelectValue placeholder="Ordenar por" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="newest">Más recientes</SelectItem>
                      <SelectItem value="oldest">Más antiguas</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            
            {/* Contador de resultados filtrados */}
            {serviceTypeFilter !== 'all' && (
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Badge variant="outline" className="bg-purple-50">
                  {filteredRequests.length} de {serviceRequests.length} solicitudes
                </Badge>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setServiceTypeFilter('all')}
                  className="text-purple-600 hover:text-purple-800"
                >
                  Limpiar filtro
                </Button>
              </div>
            )}
            
            {filteredRequests.length === 0 ? (
              <Card className="p-8 text-center border-slate-200">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">
                  {serviceTypeFilter !== 'all' 
                    ? 'No hay solicitudes para este tipo de servicio' 
                    : 'No hay solicitudes disponibles en este momento'}
                </p>
                <p className="text-sm text-slate-400 mt-2">
                  {serviceTypeFilter !== 'all' 
                    ? 'Intenta con otro filtro o revisa más tarde' 
                    : 'Las nuevas solicitudes aparecerán aquí'}
                </p>
              </Card>
            ) : (
              <div className="space-y-4">
                {filteredRequests.map((request) => {
                  const alreadyBid = myBids.some(b => b.service_request_id === request.id);
                  const isExpanded = expandedRequest === request.id;
                  const clientInfo = request.client_info;
                  const vehicleInfo = request.vehicle_info;
                  const vehicleHistory = request.vehicle_history || [];
                  const clientStats = request.client_stats || {};
                  const isNew = request.is_new;
                  const hasLocation = request.location?.lat && request.location?.lng;
                  
                  return (
                    <Card key={request.id} className={`border-slate-200 hover:shadow-md transition-shadow overflow-hidden ${isNew ? 'ring-2 ring-purple-400 ring-opacity-50' : ''}`} data-testid={`request-card-${request.id}`}>
                      {/* Indicador de solicitud nueva */}
                      {isNew && (
                        <div className="bg-purple-600 text-white px-4 py-1.5 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Sparkles className="w-4 h-4" />
                            <span className="text-sm font-medium">Nueva solicitud - No vista</span>
                          </div>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-white hover:bg-purple-700 h-6 px-2 text-xs"
                            onClick={() => markRequestAsViewed(request.id)}
                          >
                            Marcar como vista
                          </Button>
                        </div>
                      )}
                      
                      {/* Header de la solicitud */}
                      <div className="p-6">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center flex-wrap gap-2 mb-2">
                              <h4 className="font-semibold text-lg text-slate-900">{request.service_type}</h4>
                              <Badge className={getStatusColor(request.urgency === 'urgent' ? 'cancelled' : request.urgency === 'flexible' ? 'completed' : 'pending')}>
                                {request.urgency === 'urgent' ? 'Urgente' : request.urgency === 'flexible' ? 'Flexible' : 'Normal'}
                              </Badge>
                              {request.is_insurance_claim && (
                                <Badge className="bg-blue-100 text-blue-700">
                                  <Shield className="w-3 h-3 mr-1" />
                                  Seguro
                                </Badge>
                              )}
                              {isNew && (
                                <Badge className="bg-purple-100 text-purple-700 animate-pulse">
                                  <Sparkles className="w-3 h-3 mr-1" />
                                  Nueva
                                </Badge>
                              )}
                              {!isNew && request.bids_count === 0 && (
                                <Badge className="bg-green-100 text-green-700">Sin ofertas</Badge>
                              )}
                            </div>
                            <p className="text-slate-600 mb-3">{request.description}</p>
                            
                            {/* Info básica en una línea */}
                            <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                              <span className="flex items-center">
                                <Clock className="w-4 h-4 mr-1" />
                                {formatDate(request.created_at)}
                              </span>
                              <span className="flex items-center">
                                <Users className="w-4 h-4 mr-1" />
                                {request.bids_count} ofertas
                              </span>
                              {vehicleInfo && (
                                <span className="flex items-center font-medium text-slate-700">
                                  <Car className="w-4 h-4 mr-1" />
                                  {vehicleInfo.make} {vehicleInfo.model} {vehicleInfo.year}
                                </span>
                              )}
                              {hasLocation && (
                                <span className="flex items-center text-purple-600">
                                  <MapPin className="w-4 h-4 mr-1" />
                                  {request.location.city || request.location.address || 'Ver mapa'}
                                </span>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            {/* Botón expandir/colapsar */}
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => {
                                const newExpanded = isExpanded ? null : request.id;
                                setExpandedRequest(newExpanded);
                                // Mark as viewed when expanded
                                if (newExpanded && isNew) {
                                  markRequestAsViewed(request.id);
                                }
                              }}
                              className="text-slate-500 hover:text-purple-600"
                              data-testid={`expand-btn-${request.id}`}
                            >
                              {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                              <span className="ml-1 text-sm">{isExpanded ? 'Menos' : 'Más info'}</span>
                            </Button>
                            
                            {/* Botón de Valuación de Daños (solo para solicitudes de seguro) */}
                            {request.is_insurance_claim && (
                              <Button
                                variant="outline"
                                onClick={() => {
                                  setSelectedRequestForValuation(request);
                                  setValuationModalOpen(true);
                                }}
                                className="border-blue-300 text-blue-600 hover:bg-blue-50"
                                data-testid={`valuation-btn-${request.id}`}
                              >
                                <Shield className="w-4 h-4 mr-1" />
                                Valuación
                              </Button>
                            )}
                            
                            {alreadyBid ? (
                              <Badge className="bg-slate-100 text-slate-600">Ya cotizaste</Badge>
                            ) : (
                              <Dialog open={selectedRequest === request.id} onOpenChange={(open) => !open && setSelectedRequest(null)}>
                                <DialogTrigger asChild>
                                  <Button onClick={() => setSelectedRequest(request.id)} className="bg-purple-600 hover:bg-purple-700" data-testid={`bid-btn-${request.id}`}>
                                    Hacer Oferta
                                  </Button>
                                </DialogTrigger>
                                <DialogContent className="sm:max-w-md">
                                  <DialogHeader>
                                    <DialogTitle>Enviar Cotización</DialogTitle>
                                  </DialogHeader>
                                  <div className="space-y-4 mt-4">
                                    <div className="bg-purple-50 rounded-lg p-3 text-sm">
                                      <p className="font-medium text-purple-900">{request.service_type}</p>
                                      <p className="text-purple-700">{request.description}</p>
                                      {vehicleInfo && (
                                        <p className="text-purple-600 mt-1 flex items-center">
                                          <Car className="w-3 h-3 mr-1" />
                                          {vehicleInfo.make} {vehicleInfo.model} {vehicleInfo.year} - {vehicleInfo.license_plate}
                                        </p>
                                      )}
                                    </div>
                                    <div>
                                      <label className="text-sm font-medium text-slate-700">Precio (Colones)</label>
                                      <Input
                                        type="number"
                                        value={bidForm.price}
                                        onChange={(e) => setBidForm({ ...bidForm, price: e.target.value })}
                                        placeholder="45000"
                                        className="mt-1"
                                        data-testid="bid-price-input"
                                      />
                                    </div>
                                    <div>
                                      <label className="text-sm font-medium text-slate-700">Tiempo Estimado</label>
                                      <Input
                                        value={bidForm.estimated_time}
                                        onChange={(e) => setBidForm({ ...bidForm, estimated_time: e.target.value })}
                                        placeholder="2 horas"
                                        className="mt-1"
                                        data-testid="bid-time-input"
                                      />
                                    </div>
                                    <div>
                                      <label className="text-sm font-medium text-slate-700">Mensaje al Cliente</label>
                                      <Textarea
                                        value={bidForm.message}
                                        onChange={(e) => setBidForm({ ...bidForm, message: e.target.value })}
                                        placeholder="Describe tu oferta, incluye garantías, repuestos, etc."
                                        rows={4}
                                        className="mt-1"
                                        data-testid="bid-message-input"
                                      />
                                    </div>

                                    <div>
                                      <label className="text-sm font-medium text-slate-700">Fechas propuestas para la cita</label>
                                      <p className="text-xs text-slate-500 mb-2">El cliente elegirá una de estas fechas si acepta tu oferta.</p>
                                      <div className="flex gap-2">
                                        <Input
                                          type="datetime-local"
                                          value={newProposedDate}
                                          onChange={(e) => setNewProposedDate(e.target.value)}
                                          className="flex-1"
                                          data-testid="bid-proposed-date-input"
                                        />
                                        <Button type="button" onClick={addProposedDate} variant="outline" data-testid="add-proposed-date-btn">
                                          + Añadir
                                        </Button>
                                      </div>
                                      {bidForm.proposed_dates.length > 0 && (
                                        <div className="flex flex-wrap gap-2 mt-2">
                                          {bidForm.proposed_dates.map((d) => (
                                            <Badge
                                              key={d}
                                              variant="outline"
                                              className="px-2 py-1 cursor-pointer hover:bg-red-50"
                                              onClick={() => removeProposedDate(d)}
                                              data-testid={`proposed-date-chip-${d}`}
                                            >
                                              {new Date(d).toLocaleString('es-CR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                              <span className="ml-1 text-red-500">×</span>
                                            </Badge>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                    <Button onClick={() => handleSubmitBid(request.id)} className="w-full bg-purple-600 hover:bg-purple-700" data-testid="submit-bid-btn">
                                      Enviar Cotización
                                    </Button>
                                  </div>
                                </DialogContent>
                              </Dialog>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Sección expandible con información detallada */}
                      {isExpanded && (
                        <div className="border-t border-slate-200 bg-slate-50">
                          <div className="p-6 grid md:grid-cols-3 gap-6">
                            {/* Información del Cliente */}
                            <div className="space-y-3">
                              <h5 className="font-semibold text-slate-900 flex items-center">
                                <User className="w-4 h-4 mr-2 text-purple-600" />
                                Información del Cliente
                              </h5>
                              {clientInfo ? (
                                <div className="bg-white rounded-lg p-4 border border-slate-100 space-y-2">
                                  <p className="font-medium text-slate-900">{clientInfo.name}</p>
                                  {clientInfo.email && (
                                    <p className="text-sm text-slate-600 flex items-center">
                                      <Mail className="w-3 h-3 mr-2" />
                                      {clientInfo.email}
                                    </p>
                                  )}
                                  {clientInfo.phone && (
                                    <p className="text-sm text-slate-600 flex items-center">
                                      <Phone className="w-3 h-3 mr-2" />
                                      {clientInfo.phone}
                                    </p>
                                  )}
                                  <div className="pt-2 border-t border-slate-100 mt-2">
                                    <p className="text-xs text-slate-500">
                                      Cliente desde: {formatDate(clientInfo.created_at)}
                                    </p>
                                    <div className="flex items-center gap-3 mt-1">
                                      <Badge variant="outline" className="text-xs">
                                        {clientStats.total_requests || 0} solicitudes
                                      </Badge>
                                      <Badge variant="outline" className="text-xs bg-green-50 text-green-700">
                                        {clientStats.completed_requests || 0} completadas
                                      </Badge>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <p className="text-sm text-slate-500">Información no disponible</p>
                              )}
                            </div>
                            
                            {/* Información del Vehículo */}
                            <div className="space-y-3">
                              <h5 className="font-semibold text-slate-900 flex items-center">
                                <Car className="w-4 h-4 mr-2 text-purple-600" />
                                Detalles del Vehículo
                              </h5>
                              {vehicleInfo ? (
                                <div className="bg-white rounded-lg p-4 border border-slate-100 space-y-2">
                                  <p className="font-medium text-slate-900">
                                    {vehicleInfo.make} {vehicleInfo.model}
                                  </p>
                                  <div className="grid grid-cols-2 gap-2 text-sm">
                                    <div>
                                      <p className="text-slate-500">Año</p>
                                      <p className="font-medium">{vehicleInfo.year}</p>
                                    </div>
                                    <div>
                                      <p className="text-slate-500">Placa</p>
                                      <p className="font-medium">{vehicleInfo.license_plate}</p>
                                    </div>
                                    <div>
                                      <p className="text-slate-500">Color</p>
                                      <p className="font-medium">{vehicleInfo.color}</p>
                                    </div>
                                    <div>
                                      <p className="text-slate-500">Kilometraje</p>
                                      <p className="font-medium flex items-center">
                                        <Gauge className="w-3 h-3 mr-1" />
                                        {vehicleInfo.mileage?.toLocaleString()} km
                                      </p>
                                    </div>
                                  </div>
                                  {vehicleInfo.vin && (
                                    <div className="pt-2 border-t border-slate-100">
                                      <p className="text-xs text-slate-500">VIN: {vehicleInfo.vin}</p>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <p className="text-sm text-slate-500">Información no disponible</p>
                              )}
                            </div>
                            
                            {/* Ubicaciones Preferidas y Detalles Adicionales */}
                            <div className="space-y-3">
                              <h5 className="font-semibold text-slate-900 flex items-center">
                                <MapPin className="w-4 h-4 mr-2 text-purple-600" />
                                Ubicaciones Preferidas
                              </h5>
                              <div className="bg-white rounded-lg p-4 border border-slate-100 space-y-2">
                                {request.preferred_locations && request.preferred_locations.length > 0 ? (
                                  <ul className="space-y-2">
                                    {request.preferred_locations.map((loc, idx) => (
                                      <li key={`loc-${request.id}-${loc.address || loc}-${idx}`} className="flex items-start text-sm">
                                        <Badge variant="outline" className="mr-2 text-xs">
                                          {idx + 1}º
                                        </Badge>
                                        <span className="text-slate-700">{loc.address || loc}</span>
                                      </li>
                                    ))}
                                  </ul>
                                ) : request.location ? (
                                  <p className="text-sm text-slate-700">
                                    {request.location.address || request.location.city || 'Costa Rica'}
                                  </p>
                                ) : (
                                  <p className="text-sm text-slate-500">No especificado</p>
                                )}
                                
                                {/* Fechas preferidas */}
                                {request.preferred_dates && request.preferred_dates.length > 0 && (
                                  <div className="pt-2 border-t border-slate-100 mt-2">
                                    <p className="text-xs font-medium text-slate-600 mb-1">Fechas preferidas:</p>
                                    <div className="flex flex-wrap gap-1">
                                      {request.preferred_dates.map((date) => (
                                        <Badge key={`date-${request.id}-${date}`} variant="outline" className="text-xs bg-purple-50">
                                          <Calendar className="w-3 h-3 mr-1" />
                                          {formatDate(date)}
                                        </Badge>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Notas adicionales */}
                                {request.additional_notes && (
                                  <div className="pt-2 border-t border-slate-100 mt-2">
                                    <p className="text-xs font-medium text-slate-600 mb-1 flex items-center">
                                      <AlertCircle className="w-3 h-3 mr-1" />
                                      Notas adicionales:
                                    </p>
                                    <p className="text-sm text-slate-700 bg-yellow-50 p-2 rounded">
                                      {request.additional_notes}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {/* Mapa de ubicación */}
                          {hasLocation && (
                            <div className="px-6 pb-6">
                              <h5 className="font-semibold text-slate-900 flex items-center mb-3">
                                <MapPin className="w-4 h-4 mr-2 text-purple-600" />
                                Ubicación del Servicio
                              </h5>
                              <LocationMap 
                                position={{
                                  lat: request.location.lat,
                                  lng: request.location.lng,
                                  address: request.location.address || request.location.city
                                }}
                                height="250px"
                                zoom={15}
                                popupText={request.service_type}
                                className="shadow-sm"
                              />
                              {request.location.address && (
                                <p className="mt-2 text-sm text-slate-600 flex items-center">
                                  <MapPin className="w-4 h-4 mr-1 text-purple-500" />
                                  {request.location.address}
                                  {request.location.city && `, ${request.location.city}`}
                                </p>
                              )}
                            </div>
                          )}
                          
                          {/* Historial del Vehículo */}
                          {vehicleHistory.length > 0 && (
                            <div className="px-6 pb-6">
                              <h5 className="font-semibold text-slate-900 flex items-center mb-3">
                                <History className="w-4 h-4 mr-2 text-purple-600" />
                                Historial de Servicios del Vehículo ({vehicleHistory.length})
                              </h5>
                              <div className="bg-white rounded-lg border border-slate-100 overflow-hidden">
                                <table className="w-full text-sm">
                                  <thead className="bg-slate-50">
                                    <tr>
                                      <th className="px-4 py-2 text-left text-slate-600">Fecha</th>
                                      <th className="px-4 py-2 text-left text-slate-600">Servicio</th>
                                      <th className="px-4 py-2 text-left text-slate-600">Taller</th>
                                      <th className="px-4 py-2 text-right text-slate-600">Costo</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-100">
                                    {vehicleHistory.slice(0, 5).map((entry) => (
                                      <tr key={entry.id || `history-${entry.service_date}-${entry.service_type}`} className="hover:bg-slate-50">
                                        <td className="px-4 py-2 text-slate-700">{formatDate(entry.service_date)}</td>
                                        <td className="px-4 py-2 text-slate-900 font-medium">{entry.service_type}</td>
                                        <td className="px-4 py-2 text-slate-700">{entry.taller_name || 'N/A'}</td>
                                        <td className="px-4 py-2 text-right text-slate-900">{formatPrice(entry.cost || 0)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                                {vehicleHistory.length > 5 && (
                                  <div className="px-4 py-2 bg-slate-50 text-center text-xs text-slate-500">
                                    +{vehicleHistory.length - 5} servicios anteriores
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* My Bids Tab */}
          <TabsContent value="bids" className="space-y-4">
            <h3 className="text-xl font-semibold text-slate-900">Historial de Cotizaciones</h3>
            
            {myBids.length === 0 ? (
              <Card className="p-8 text-center border-slate-200">
                <Send className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No has enviado cotizaciones aún</p>
                <p className="text-sm text-slate-400 mt-2">Revisa las solicitudes disponibles para empezar</p>
              </Card>
            ) : (
              <div className="space-y-3">
                {sortedMyBids.map((bid) => {
                  const request = allRequests.find(r => r.id === bid.service_request_id);
                  
                  return (
                    <Card key={bid.id} className="p-5 border-slate-200 hover:shadow-md transition-shadow" data-testid={`my-bid-${bid.id}`}>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h4 className="font-bold text-xl text-purple-600">{formatPrice(bid.price)}</h4>
                            <Badge className={getStatusColor(bid.status)}>
                              {getStatusLabel(bid.status)}
                            </Badge>
                          </div>
                          {request && (
                            <p className="text-sm text-slate-700 mb-2">
                              <span className="font-medium">Servicio:</span> {request.service_type}
                            </p>
                          )}
                          <p className="text-slate-600 text-sm mb-3">{bid.message}</p>
                          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                            <span className="flex items-center">
                              <Clock className="w-4 h-4 mr-1" />
                              {bid.estimated_time}
                            </span>
                            <span className="flex items-center">
                              <Calendar className="w-4 h-4 mr-1" />
                              Enviada: {formatDate(bid.created_at)}
                            </span>
                          </div>
                        </div>
                        <div className="flex flex-col items-end space-y-2">
                          {bid.status === 'accepted' && (
                            <div className="text-green-600 text-sm flex items-center">
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Cliente aceptó tu oferta
                            </div>
                          )}
                          {bid.status === 'rejected' && (
                            <div className="text-red-600 text-sm">
                              Oferta no seleccionada
                            </div>
                          )}
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Appointments Tab */}
          <TabsContent value="appointments" className="space-y-4">
            <h3 className="text-xl font-semibold text-slate-900">Mis Citas</h3>
            
            {appointments.length === 0 ? (
              <Card className="p-8 text-center border-slate-200">
                <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No tienes citas programadas</p>
                <p className="text-sm text-slate-400 mt-2">Cuando un cliente acepte tu oferta, aparecerá aquí</p>
              </Card>
            ) : (
              <div className="space-y-3">
                {sortedAppointments.map((appointment) => (
                  <Card key={appointment.id} className={`p-5 border-l-4 ${appointment.status === 'completed' ? 'border-l-green-500' : appointment.status === 'scheduled' ? 'border-l-purple-500' : 'border-l-yellow-500'} hover:shadow-md transition-shadow`} data-testid={`appointment-${appointment.id}`}>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <Calendar className="w-5 h-5 text-purple-600" />
                          <h4 className="font-semibold text-lg text-slate-900">
                            {formatDate(appointment.scheduled_date)}
                          </h4>
                          <Badge className={getStatusColor(appointment.status)}>
                            {getStatusLabel(appointment.status)}
                          </Badge>
                        </div>
                        <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600">
                          <span>ID Cita: {appointment.id.slice(0, 8)}...</span>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-purple-300 text-purple-700 hover:bg-purple-50"
                          onClick={() => {
                            setChatClientName('Cliente');
                            setChatOpen(true);
                          }}
                          data-testid={`chat-client-${appointment.id}`}
                        >
                          <MessageCircle className="w-4 h-4 mr-1" />
                          Chat
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-purple-300 text-purple-700 hover:bg-purple-50"
                          onClick={() => setExpandedAppointment(expandedAppointment === appointment.id ? null : appointment.id)}
                          data-testid={`toggle-progress-${appointment.id}`}
                        >
                          <Wrench className="w-4 h-4 mr-1" />
                          {expandedAppointment === appointment.id ? 'Ocultar progreso' : 'Ver/actualizar progreso'}
                        </Button>
                        {appointment.status === 'completed' && (
                          <div className="text-green-600 text-sm flex items-center">
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Trabajo completado
                          </div>
                        )}
                      </div>
                    </div>

                    {expandedAppointment === appointment.id && (
                      <div className="mt-6 pt-6 border-t border-slate-200">
                        <RepairTimeline appointmentId={appointment.id} readOnly={false} />
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Reportes Tab */}
          <TabsContent value="reports" className="space-y-4">
            <TallerReportsTab API={API} sessionToken={sessionToken} />
          </TabsContent>

          {/* Configuración Tab */}
          <TabsContent value="settings" className="space-y-4">
            <TallerSettingsTab API={API} sessionToken={sessionToken} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Chat Preview Modal */}
      <ChatPreview
        isOpen={chatOpen}
        onClose={() => setChatOpen(false)}
        otherPartyName={chatClientName}
        otherPartyRole="client"
        contextInfo="Cita activa"
      />

      {/* Damage Valuation Modal */}
      <DamageValuationForm
        isOpen={valuationModalOpen}
        onClose={() => {
          setValuationModalOpen(false);
          setSelectedRequestForValuation(null);
        }}
        serviceRequest={selectedRequestForValuation}
        onSaved={() => fetchData()}
      />

      <TourGuide steps={TALLER_TOUR_STEPS} active={tourActive} onClose={() => setTourActive(false)} />
    </div>
  );
};

export default TallerDashboard;
